import React, { useMemo } from 'react';
import {
  Box, Grid, TextField, MenuItem, Typography, Paper, Divider,
} from '@mui/material';
import { catchyTextFieldSx } from '../../theme/formStyles';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import {
  CURRENCIES,
  COUNTRIES,
  DATE_FORMATS,
  LANGUAGES,
  TIMEZONES,
  normalizeCountryCode,
  countryLabel,
} from '../../i18n/localeConfig';
import { formatMoney, formatWithDatePattern } from '../../utils/localeFormat';
import { useLocale } from '../../contexts/LocaleContext';

export interface RegionalSettingsValues {
  language: string;
  currency: string;
  timezone: string;
  dateFormat: string;
  country: string;
}

interface RegionalSettingsPanelProps {
  values: RegionalSettingsValues;
  onChange: (patch: Partial<RegionalSettingsValues>) => void;
  /** Super admin platform defaults copy */
  variant?: 'platform' | 'tenant';
  /** Optional override — defaults to live draft preview */
  formatPreview?: React.ReactNode;
  labels?: {
    title?: string;
    description?: string;
    language?: string;
    currency?: string;
    timezone?: string;
    dateFormat?: string;
    country?: string;
  };
}

function resolveLabel(
  list: { code?: string; value?: string; label: string }[],
  key: string,
  field: 'code' | 'value',
) {
  const hit = list.find((item) => (field === 'code' ? item.code : item.value) === key);
  return hit?.label || key;
}

export const RegionalSettingsPanel: React.FC<RegionalSettingsPanelProps> = ({
  values,
  onChange,
  variant = 'platform',
  formatPreview,
  labels = {},
}) => {
  const {
    languages, currencies, timezones, dateFormats, countries,
  } = useLocale();

  const languageOptions = languages?.length ? languages : LANGUAGES;
  const currencyOptions = currencies?.length ? currencies : CURRENCIES;
  const timezoneOptions = timezones?.length ? timezones : TIMEZONES;
  const dateFormatOptions = dateFormats?.length ? dateFormats : DATE_FORMATS;
  const countryOptions = countries?.length ? countries : COUNTRIES;

  const safeCountry = normalizeCountryCode(values.country);
  const safeLanguage = languageOptions.some((l) => l.code === values.language)
    ? values.language
    : (languageOptions[0]?.code || 'en');
  const safeCurrency = currencyOptions.some((c) => c.code === values.currency)
    ? values.currency
    : (currencyOptions[0]?.code || 'INR');
  const safeTimezone = timezoneOptions.some((t) => t.value === values.timezone)
    ? values.timezone
    : (timezoneOptions[0]?.value || 'Asia/Kolkata');
  const safeDateFormat = dateFormatOptions.some((d) => d.value === values.dateFormat)
    ? values.dateFormat
    : (dateFormatOptions[0]?.value || 'DD/MM/YYYY');

  const title = labels.title ?? 'Regional Settings';
  const description = labels.description ?? (
    variant === 'platform'
      ? 'Default locale for new tenants. India: English, INR, IST (Asia/Kolkata), DD/MM/YYYY. Tenants can override in company settings.'
      : 'Language, currency, timezone and date format for your company. Overrides platform defaults where set.'
  );

  const livePreview = useMemo(() => {
    const money = formatMoney(9999, safeCurrency, safeLanguage, { maximumFractionDigits: 2 });
    const date = formatWithDatePattern(new Date(), safeDateFormat, safeLanguage, safeTimezone);
    return `${money} · ${date}`;
  }, [safeCurrency, safeLanguage, safeDateFormat, safeTimezone]);

  const summaryItems = [
    { value: safeLanguage, label: labels.language ?? 'Language' },
    { value: safeCurrency, label: labels.currency ?? 'Currency' },
    { value: safeTimezone, label: labels.timezone ?? 'Timezone' },
    { value: safeDateFormat, label: labels.dateFormat ?? 'Date Format' },
    { value: safeCountry, label: labels.country ?? 'Country' },
  ];

  return (
    <Box>
      <Typography variant="h6" fontWeight={700} fontSize={16} sx={{ color: brandColors.textDark, lineHeight: 1.2 }}>
        {title}
      </Typography>
      <Typography fontSize={13} sx={{ mt: 0.5, mb: 2.5, color: brandColors.textMuted, lineHeight: 1.45, maxWidth: 640 }}>
        {description}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2.5 }}>
        {summaryItems.map((item) => (
          <Paper
            key={item.label}
            elevation={0}
            sx={{
              px: 1.5,
              py: 1,
              minWidth: 100,
              borderRadius: `${RADIUS}px`,
              border: `1px solid ${brandColors.border}`,
              bgcolor: brandCss.green50,
            }}
          >
            <Typography fontSize={10} fontWeight={700} color={brandColors.textMuted} letterSpacing={0.4} textTransform="uppercase">
              {item.label}
            </Typography>
            <Typography fontSize={14} fontWeight={800} color={brandColors.primaryDark} sx={{ mt: 0.25 }}>
              {item.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={labels.language ?? 'Language'}
            fullWidth
            size="small"
            sx={catchyTextFieldSx}
            value={safeLanguage}
            onChange={(e) => onChange({ language: e.target.value })}
          >
            {languageOptions.map((l) => (
              <MenuItem key={l.code} value={l.code}>
                {l.nativeLabel ? `${l.label} (${l.nativeLabel})` : l.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={labels.currency ?? 'Currency'}
            fullWidth
            size="small"
            sx={catchyTextFieldSx}
            value={safeCurrency}
            onChange={(e) => onChange({ currency: e.target.value })}
          >
            {currencyOptions.map((c) => (
              <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={labels.timezone ?? 'Timezone'}
            fullWidth
            size="small"
            sx={catchyTextFieldSx}
            value={safeTimezone}
            onChange={(e) => onChange({ timezone: e.target.value })}
          >
            {timezoneOptions.map((tz) => (
              <MenuItem key={tz.value} value={tz.value}>{tz.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={labels.dateFormat ?? 'Date Format'}
            fullWidth
            size="small"
            sx={catchyTextFieldSx}
            value={safeDateFormat}
            onChange={(e) => onChange({ dateFormat: e.target.value })}
          >
            {dateFormatOptions.map((df) => (
              <MenuItem key={df.value} value={df.value}>{df.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <TextField
            select
            label={labels.country ?? 'Country'}
            fullWidth
            size="small"
            sx={catchyTextFieldSx}
            value={safeCountry}
            onChange={(e) => onChange({ country: e.target.value })}
          >
            {countryOptions.map((c) => (
              <MenuItem key={c.code} value={c.code}>{c.label}</MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2.5 }} />
      <Typography fontSize={11} fontWeight={700} color={brandColors.textMuted} letterSpacing={0.5} mb={1}>
        FORMAT PREVIEW
      </Typography>
      {formatPreview || (
        <Paper sx={{ p: 2, bgcolor: brandCss.green50, borderRadius: `${RADIUS}px`, border: `1px solid ${brandColors.border}` }}>
          <Typography fontSize={12} fontWeight={700} color={brandColors.textMuted}>Preview</Typography>
          <Typography fontSize={14} fontWeight={600} mt={0.5} color={brandColors.textDark}>
            {livePreview}
          </Typography>
        </Paper>
      )}

      <Typography fontSize={11} color="text.secondary" sx={{ mt: 2, opacity: 0.85 }}>
        {resolveLabel(languageOptions, safeLanguage, 'code')}
        {' · '}
        {resolveLabel(currencyOptions, safeCurrency, 'code')}
        {' · '}
        {resolveLabel(timezoneOptions, safeTimezone, 'value')}
        {' · '}
        {resolveLabel(dateFormatOptions, safeDateFormat, 'value')}
        {' · '}
        {countryLabel(safeCountry)}
      </Typography>
    </Box>
  );
};
